import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, { logAnalyticsEvent } from '../lib/api';
import { broadcastPlanChanged } from '../lib/planSync';
import { insufficientCreditsCopy } from '../lib/insufficientCreditsCopy';
import { toast } from 'sonner';
import { Loader2, CircleCheckBig, ArrowRight } from 'lucide-react';
import CreditPackModal from '../components/CreditPackModal';
import { useReportarCapa } from '../lib/pager/pane';
import LegalAcceptance from '../components/LegalAcceptance';
// PAGER-1 — el contrato montada/activa (la doctrina completa vive en el módulo).
import { useRaizActiva } from '../lib/pager/activacion';
// TANDA AS-3 — el carril de compra nativo. En web `IS_APP_BUILD` es false y NADA de esto
// se ejecuta: el módulo de StoreKit ni siquiera se evalúa (lib/purchases.js hace el
// import dinámico dentro de su propia rama).
import { IS_APP_BUILD } from '../lib/buildTarget';
import * as purchases from '../lib/purchases';
import { appleErrorCopy } from '../lib/appleErrorCopy';
import { fuenteDePro } from '../lib/fuenteDePro';
// TANDA 28b (firma #230) — referidos. En web y con la bandera prendida esta
// pagina pinta la franja del descuento y el precio tachado a quien YA gano uno.
// LAPIDA post-t7-2c (E1): murieron la linea discreta "Invita a un amigo" y su
// hermana de la ventana de exito, con la ventana `InvitaAmigoModal` que abrian;
// `pricing:referral.banner_discount` y la nota del precio SE QUEDAN.
import {
  fetchReferralState, resetReferralState, useVistaReferidos, datosDeVista,
} from '../lib/referidos';

// House palette (existing tokens): #FF6B00 primary orange, #ff8c32 card accent, #22c55e success/save green.
const ORANGE = '#FF6B00';
const ORANGE_ACCENT = '#ff8c32';
const GREEN = '#22c55e';
const GREEN_BG = 'rgba(34,197,94,0.12)';

// 11 features preserved; only the numeric capacities changed and the premium column is gone.
//
// AS-6d — el renglón de OFFLINE, arriba de todo. El modo offline es una capacidad exclusiva
// de Pro (el candado vive en `lib/proGate.js` + `OfflineProModal`) y hasta hoy no aparecía en
// NINGUNA tabla de precios: se cobraba por algo que no estaba en la lista.
//
// El texto cambia por build porque la WEB no tiene modo offline —el offline vive en la bodega
// cifrada de la app—, así que un "Full offline studying" a secas en el navegador prometería
// algo falso. La coletilla "in our app" lo hace verdad en la web y de paso empuja la descarga;
// en la app la coletilla sobra. Esta tabla es la COMPARTIDA app/web, así que la variante la
// elige `IS_APP_BUILD` (mismo gate estándar del resto del repo). `/pricing` es 100% web y
// por eso ahí la clave va fija — no se gatea de más.
//
// `free: false` es el molde de la casa para lo exclusivo de Pro (idéntico a
// `image_diagram_analysis`): en la tarjeta Free el renglón se pinta con TACHE y gris apagado.
// Ese es justamente el candado anunciándose — jamás con palomita en Free.
//
// precios-sabi (firmas #713 y #714, 25-oct): la fila de Sabi es la UNICA con texto por plan
// (`porPlan`): la tarjeta Free pinta `features.tutor_free` y la Pro `features.tutor_pro`, con
// palomita en las dos y sin numero en negritas (la palabra "Sabi" si va en negritas: ver
// `conSabiEnNegritas`). Las demas filas se pintan como siempre.
const FEATURES = [
  { key: IS_APP_BUILD ? 'offline_studying_app' : 'offline_studying_web', free: false, pro: true },
  { key: 'processing_credits',         free: '5',   pro: '50' },
  { key: 'active_documents',           free: '5',   pro: '30' },
  { key: 'tutor',                      free: true,  pro: true,  porPlan: true },
  { key: 'adaptive_study_mode',        free: true,  pro: true },
  { key: 'exam_simulator',             free: true,  pro: true },
  { key: 'flashcards',                 free: true,  pro: true },
  { key: 'full_adaptive_algorithm',    free: true,  pro: true },
  { key: 'document_progress_tracking', free: true,  pro: true },
  { key: 'per_document_analytics',     free: true,  pro: true },
  { key: 'image_diagram_analysis',     free: false, pro: true },
  { key: 'topic_mastery_tracking',     free: true,  pro: true },
  { key: 'exam_readiness_indicator',   free: true,  pro: true },
];

// precios-sabi, remate (firma #715): en la fila de Sabi SOLO la palabra "Sabi" va en negritas y en
// blanco, con el estilo de los numeros de esta tabla. Un texto que no empieza con "Sabi" se pinta
// como siempre; los locales no llevan marca ninguna.
function conSabiEnNegritas(texto) {
  if (typeof texto !== 'string' || !texto.startsWith('Sabi')) return texto;
  return (
    <>
      <span style={{ color: '#fff', fontWeight: 700 }}>Sabi</span>
      {texto.slice(4)}
    </>
  );
}

const PLANS = [
  { key: 'free', featured: false },
  { key: 'pro',  featured: true },
];

const PLAN_ORDER = { free: 0, pro: 1 };

// Pro pricing per billing interval (USD). Annual shows the per-month equivalent + the yearly total.
//
// AS-3 — ESTAS DOS CONSTANTES SON DE LA **WEB**. En build de app jamás se pintan, ni
// siquiera como respaldo: allá el precio lo pone Apple (localizado por storefront) y los
// derivados se recalculan con Intl.NumberFormat sobre `price` + `currencyCode` de StoreKit.
const PRO_PRICING = {
  monthly: { display: '$8.99' },
  annual:  { display: '$7.49' },  // 89.90 / 12
};

// Los importes de la WEB que viajan a las claves con cifra (ver PRO_PRICING). Antes vivían
// dentro del texto de `pricing:annual.*`; ahora el texto lleva placeholder para que la app
// pueda poner el de Apple, y la web pone EXACTAMENTE los mismos números de siempre.
const WEB_ANNUAL_TOTAL = '$89.90';
const WEB_ANNUAL_SAVE = '$17.98';
// ajustes-1-3-0 (Bloque A3, #497): aqui vivia `WEB_PACK_PER_CREDIT = '$0.22'`, el precio
// por credito del chip "Best value". El chip dice solo "Best value" / "Mejor valor"
// (`pricing:packs.best_value`, sin placeholder), asi que la constante se retiro.

// Credit packs shown below the grid (mirror of CREDIT_PACK_CONFIG small/large).
// `key` es la clave interna del producto de Apple (lib/purchases.js), aditiva: la web no
// la mira.
const PACKS_DISPLAY = [
  { credits: 15, price: '4.99', best: false, key: 'pack_small' },
  { credits: 40, price: '8.99', best: true,  key: 'pack_large' },
];

function CheckIcon() {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(255,140,50,0.1)',
      }}
    >
      <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke={ORANGE_ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

function XIcon() {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
        <path d="M1 1L7 7M7 1L1 7" stroke="#2a2a2a" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </span>
  );
}

export default function PricingPage() {
  // PAGER-1 — ¿el usuario está viendo ESTA raíz? Sin proveedor arriba: `true`,
  // que es la verdad de hoy (el router monta una sola raíz). En PAGER-2 el
  // proveedor lo pone el pager por encima. Ver lib/pager/activacion.js.
  const activa = useRaizActiva();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['pricing', 'dashboard', 'common', 'modals']);
  const [loading, setLoading] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [upgradeSuccess, setUpgradeSuccess] = useState(null); // {plan, amount_charged, monthly_price}
  // Full-screen blocking overlay shown ONLY when /payments/status polling fires from an
  // auto-split subscription-upgrade flow. Prevents user navigation that would unmount this
  // page and cancel polling, breaking the AutoSplitModal-restoration handoff.
  const [autoSplitOverlayVisible, setAutoSplitOverlayVisible] = useState(false);

  /* ── TANDA 28b (firma #230) — EL ESTADO DE REFERIDOS ───────────────────────
     UNA peticion por carga de pagina, y ni esa: `fetchReferralState` guarda la
     promesa en un memo de modulo, asi que si el menu lateral ya la pidio esto
     comparte su respuesta. No bloquea el pintado — la pagina sale como hoy y
     las superficies aparecen cuando llega. En build de app la funcion sale por
     lo bajo sin tocar la red.
     En modo vista NO se pide nada: se usan los datos de mentira del bloque I. */
  const vistaReferidos = useVistaReferidos(user);
  const deVista = datosDeVista(vistaReferidos);
  // Booleano y no el objeto: `datosDeVista` devuelve una copia nueva en cada
  // render, y como dependencia volveria a disparar el efecto siempre (mismo
  // racional que en `AvisoReferidos.js`, donde ademas se veia en pantalla).
  const enVistaRef = Boolean(deVista);
  /* 28b-fix (H-2) — LA VISTA SE DERIVA AL PINTAR, NO SE COPIA A ESTADO.
     Antes esto era `useState(() => (deVista ? deVista.state : null))`, y el
     inicializador de `useState` corre UNA sola vez: si en el primer render la
     sesión todavía no había resuelto, la puerta de administrador estaba cerrada,
     el estado nacía en `null` y el efecto que lo llenaría sale temprano con
     `if (enVistaRef) return`. Medido en el censo: `/upgrade` perdía franja,
     tachado Y línea discreta, mientras el menú lateral sí las pintaba porque él
     ya derivaba al pintar (`Sidebar.js:120-122`). Esa asimetría es la forma
     exacta del reporte de A.4 del smoke.
     `refState` queda SÓLO para la respuesta de la red. */
  const [refState, setRefState] = useState(null);
  const estadoReferidos = deVista ? deVista.state : refState;
  // 28b-fix (H-1): `/state` va por CUENTA. El memo de modulo guarda el `userId`
  // con el que se pidio, asi que una sesion nueva en la misma carga de pagina no
  // hereda la franja ni el tachado de la anterior.
  const userIdRef = user?.id;
  useEffect(() => {
    if (enVistaRef || IS_APP_BUILD) return undefined;
    let vivo = true;
    fetchReferralState(userIdRef).then((s) => { if (vivo) setRefState(s); });
    return () => { vivo = false; };
  }, [enVistaRef, userIdRef]);
  /* ── TANDA 28d (§E) — `?vista=exito` ABRE LA VENTANA «¡YA ERES PRO!» ───────
     La 28b-fix3 hizo que la ventana se abriera al volver de Stripe, pero la
     única forma de VERLA sigue siendo pagar $8.99 de verdad y volver con un
     `session_id` vivo. Y la línea de invitar del diseño §7.6 vive DENTRO de
     ella: es la superficie que más caro cuesta mirar de las ocho.

     EFECTO PROPIO, Y ESO ES LO QUE MÁS IMPORTA. No se toca ni una línea del
     efecto de `session_id`: la vista no entra al carril del cobro ni para
     leerlo — ni poll, ni `/payments/status`, ni `replaceState`, ni
     `refreshUser`. Un modo de diagnóstico que se cuela en el camino del dinero
     deja de ser diagnóstico.

     `sinCuerpo: true` por lo mismo que en la vuelta real: `body_default` lleva
     «/month» LITERAL fuera del placeholder y le diría «$8.99/mes» a quien pagó
     el ANUAL. Título, línea de invitar y botones. Cero copy nuevo.

     Se dispara UNA vez por encendido de la vista: cerrada a mano (por «Ir al
     panel» o por «Ver cómo →») no se vuelve a abrir sola, porque `vistaReferidos`
     no cambia. */
  useEffect(() => {
    if (vistaReferidos !== 'exito') return;
    setUpgradeSuccess({ plan: 'pro', sinCuerpo: true });
  }, [vistaReferidos]);

  const referidosOn = Boolean(estadoReferidos?.enabled);
  const descuentoPendiente = referidosOn && (estadoReferidos?.discount_pending || 0) > 0;
  /* EL TACHADO ES SOLO DE LA WEB: en build de app el precio lo pone Apple y un
     descuento nuestro sobre su cifra sería, además de falso, la clase de cosa por
     la que 3.1.1 rechaza un build. */
  const tacharPrecio = descuentoPendiente && !IS_APP_BUILD;
  /* El precio con el 20% quitado, calculado A PARTIR DEL LITERAL que ya está
     pintado. Si el string no trae un número reconocible se devuelve `null` y el
     tachado no se pinta: antes un hueco que un «$NaN». */
  const conDescuento = (texto) => {
    const n = parseFloat(String(texto).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n)) return null;
    return `$${(n * 0.8).toFixed(2)}`;
  };

  // Normalize: an unknown plan (e.g. a residual 'premium' after retirement) isn't in PLAN_ORDER,
  // which would render "Downgrade" on both cards. Treat any unknown plan as 'free' for the buttons.
  const rawPlan = user?.plan || 'free';
  const currentPlan = PLAN_ORDER[rawPlan] !== undefined ? rawPlan : 'free';
  /* ── TANDA 28d (§D) — EL PLAN CON EL QUE SE PINTA LA TARJETA PRO ───────────
     Carlos es Pro, y la firma #262 dice que un Pro NO ve el precio tachado: su
     tarjeta dice «Plan actual» y una oferta de compra encima sería una oferta
     que no puede tomar. Consecuencia medida: con `?vista=amigo` puesto veía la
     franja pero NUNCA el tachado, que es justo la pieza que el modo vista
     existe para enseñar.
     Una sola derivación, y SÓLO la consume la tarjeta Pro (`isCurrent` /
     `isUpgrade`, y de ahí `mostrarActual`, `conDescuentoEnLaTarjeta` —el
     tachado de #263 y su nota—, el sufijo y `annual-detail`). La franja, la
     línea discreta, los paquetes y el JSON-LD siguen leyendo lo real.
     Sin `?vista=` esto es `currentPlan` y la página es byte-idéntica a la de
     hoy: `enVistaRef` ya es `false` para cualquiera que no sea administrador en
     web (`esVistaAdmin` + `!IS_APP_BUILD`, lib/referidos.js:329). */
  const planParaPintar = enVistaRef ? 'free' : currentPlan;
  const isGuest = user?.is_guest;
  const pricingViewedRef = useRef(false);

  /* ── AS-3 — EL CATÁLOGO DE STOREKIT (sólo build de app) ─────────────────────
     `iap` es el estado observable de lib/purchases.js: loading | ready | error, más los
     cuatro productos y cuál suscripción está vigente en ESTE aparato. En web queda null
     de por vida y todo lo que cuelga de él es inerte.
     Colgado de `activa` por la doctrina PAGER-1: una raíz dormida no trabaja. */
  const [iap, setIap] = useState(() => (IS_APP_BUILD ? purchases.snapshot() : null));
  const [restoring, setRestoring] = useState(false);
  useEffect(() => {
    if (!IS_APP_BUILD || !activa) return undefined;
    const off = purchases.subscribeProducts(setIap);
    purchases.loadProducts();
    return off;
  }, [activa]);

  const iapStatus = IS_APP_BUILD ? (iap?.status || 'idle') : null;
  const iapReady = IS_APP_BUILD && iapStatus === 'ready';
  const iapFailed = IS_APP_BUILD && iapStatus === 'error';
  const producto = (clave) => (IS_APP_BUILD ? (iap?.products || {})[clave] : null);

  /* El formateador de los DERIVADOS (el por-mes del anual, el ahorro, el precio por
     crédito): moneda y locale reales, nunca un símbolo hardcodeado. Si el entorno no
     puede formatear, devuelve el número pelado antes que romper la pantalla. */
  const dinero = (valor, moneda) => {
    try {
      return new Intl.NumberFormat(i18n?.language || undefined,
        { style: 'currency', currency: moneda }).format(valor);
    } catch (e) {
      return `${valor}`;
    }
  };

  const proMensual = producto('pro_monthly');
  const proAnual = producto('pro_annual');
  // REMATES-1-2-1 (remate, #458): manda la fuente VIVA (`billing_source`), con la misma
  // caída a `billing_provider` que Ajustes si el campo no viene.
  const provider = fuenteDePro(user);
  // F-C: quien paga Pro por nuestro sitio web no compra la suscripción otra vez aquí.
  const stripeManaged = IS_APP_BUILD && provider === 'stripe';
  // F-H: con proveedor Apple, el intervalo que el aparato NO tiene se puede comprar —
  // Apple pinta su hoja de crossgrade y hace el prorrateo. Cero UI nueva.
  const appleProvider = IS_APP_BUILD && provider === 'apple';
  const idSeleccionado = purchases.APPLE_PRODUCT_IDS[billing === 'annual' ? 'pro_annual' : 'pro_monthly'];
  const idVigente = IS_APP_BUILD ? (iap?.entitledProductId || null) : null;

  /* Los tres DERIVADOS con cifra. En web son las constantes de siempre (texto pintado
     idéntico); en app salen de StoreKit y de nadie más. `null` = todavía no hay precio
     que pintar, y entonces la línea no se pinta (nunca con el número de la web). */
  const totalAnual = IS_APP_BUILD
    ? ((iapReady && proAnual) ? proAnual.priceString : null)
    : WEB_ANNUAL_TOTAL;
  const ahorroAnual = IS_APP_BUILD
    ? ((iapReady && proAnual && proMensual)
        ? dinero(Math.max(0, (proMensual.price * 12) - proAnual.price), proAnual.currencyCode)
        : null)
    : WEB_ANNUAL_SAVE;
  /* APPLE 3.1.2(c) (#100a) — el por-mes del anual. Es EL MISMO numero de siempre y sale
     de la misma fuente; lo que cambio es su sitio: antes era el tipo grande de la
     tarjeta y ahora es la linea subordinada de abajo. El que sube al tipo grande es
     `totalAnual`, el monto FACTURADO, que es lo que Apple exige que sea lo mas claro y
     conspicuo. Cero cifras nuevas: se reordena que campo es protagonista. */
  const porMesAnual = IS_APP_BUILD
    ? ((iapReady && proAnual) ? dinero(proAnual.price / 12, proAnual.currencyCode) : null)
    : PRO_PRICING.annual.display;
  // ajustes-1-3-0 (Bloque A3, #497): aqui vivia `porCredito` (el precio del paquete grande
  // entre sus creditos, de Apple en la app y `$0.22` en la web) para el chip "Best value".
  // El chip ya no lleva cifra: se retiro con su calculo y con `paqueteGrande`, que solo
  // lo alimentaba a el.

  /* PAGER-1 — EL ESCALÓN DEL EMBUDO QUE MÁS TENÍA QUE PERDER.
     `pricing_viewed` es un paso del funnel de admin contado por usuarios
     únicos, y es el ÚNICO que mide INTENCIÓN DE COMPRA: pre-montada, esta raíz
     lo dispararía para todo usuario que abriera la app y el escalón se pegaría
     a ~100 %, dejando de medir nada. Colgado de la activación vuelve a
     significar "el usuario fue a ver precios". El ref se resetea en el cleanup
     de la DESACTIVACIÓN (misma semántica por visita que las otras dos raíces).
     Con `activa` fija en true: emite al montar, cleanup sólo al desmontar. */
  useEffect(() => {
    if (!activa) return undefined;
    if (!pricingViewedRef.current) {
      pricingViewedRef.current = true;
      logAnalyticsEvent('pricing_viewed');
    }
    return () => { pricingViewedRef.current = false; };
  }, [activa]);

  /* Stripe payment polling — extended: if user came from auto-split flow, redirect to /dashboard
     so the AutoSplitModal can be restored. Otherwise behavior unchanged.

     PAGER-1 — LA COSA MÁS PELIGROSA QUE UNA RAÍZ INVISIBLE PUEDE HACER. Este
     efecto lee la URL DEL CHASIS (`?session_id`), arma un poll de 2 s SIN TOPE
     DE INTENTOS y, al cobrar, NAVEGA (`navigate` / `window.location.href`).
     Pre-montado y dormido eso son 30 peticiones por minuto indefinidas y una
     navegación desde una pantalla que el usuario no está viendo. Corre SÓLO
     mientras la raíz esté activa; el cleanup del intervalo ya existía y ahora
     también se dispara al desactivarse. Comportamiento de hoy IDÉNTICO: con
     `?session_id` el usuario aterriza en esta ruta, o sea activo. */
  useEffect(() => {
    if (!activa) return undefined;
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Show the blocking overlay ONLY when this polling round was initiated by an
      // auto-split subscription upgrade. The flag is read once here (intent doesn't change
      // mid-poll) and rendered as a sibling overlay below.
      try {
        const raw = sessionStorage.getItem('studybuddy_auto_split_intent');
        const intent = raw ? JSON.parse(raw) : null;
        if (intent?.intent_type === 'subscription_upgrade') {
          setAutoSplitOverlayVisible(true);
        }
      } catch { /* ignored — overlay stays hidden */ }

      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/payments/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            clearInterval(poll);
            toast.success(t('pricing:toast.upgraded', { plan: t(`pricing:plans.${res.data.plan}.name`) }));
            broadcastPlanChanged();  // nudge dashboard tabs to refetch (unarchive lags the webhook)
            /* 28b-fix3 — MISMA LLAMADA, MISMO SITIO: sólo se guarda la promesa para
               poder esperarla más abajo. La rama del auto-split la sigue dejando
               correr sin esperar, exactamente como hasta hoy. */
            const perfilFresco = refreshUser();
            // Auto-split return flow: if user upgraded as part of an auto-split intent,
            // hand off to /dashboard with the from_auto_split flag so Dashboard's
            // intent-detection effect can restore the modal.
            let intent = null;
            try {
              const raw = sessionStorage.getItem('studybuddy_auto_split_intent');
              if (raw) intent = JSON.parse(raw);
            } catch { /* ignored */ }
            // Hide overlay BEFORE redirect/navigate so the brief paint between state change
            // and navigation doesn't show a stuck overlay.
            setAutoSplitOverlayVisible(false);
            if (intent?.intent_type === 'subscription_upgrade') {
              window.location.href = '/dashboard?from_auto_split=true';
              return;
            }
            /* TANDA 28b-fix3 (bloque B, censo §2) — LA VENTANA «¡YA ERES PRO!»,
               QUE EN ESTE CAMINO NUNCA SE HABÍA ABIERTO.

               `setUpgradeSuccess` sólo se llamaba desde la compra nativa de Apple y
               desde la rama `status: 'upgraded'` (la mejora INSTANTÁNEA de quien ya
               tiene suscripción viva). Un free que compra Pro se va a Stripe, y al
               volver lo único que veía era el toast de arriba seguido de un
               `navigate('/dashboard')` que le cambiaba la pantalla debajo. De paso, la
               línea de invitar del diseño §7.6 vive DENTRO de este modal: era una de
               las ocho superficies, construida y sin camino que llegara a ella.

               1. `replaceState` PRIMERO. Sin esto un F5 vuelve a montar el poll con el
                  mismo `session_id`, `/payments/status` contesta `paid` de inmediato
                  (lo lee de la fila ya cobrada) y la ventana se reabre sobre una compra
                  vieja. Va antes que nada, no al final.
               2. `await refreshUser()` (antes iba sin `await`) para que el plan ya esté
                  fresco cuando se pinte, y `resetReferralState()` porque el
                  `discount_pending` acaba de gastarse.
               3. La ventana en vez del `navigate`: el botón «Ir al panel» ya vive
                  dentro, así que el usuario llega igual, con un clic suyo.

               `sinCuerpo` porque `/payments/status` sólo devuelve `{status,
               payment_status, plan}`: sin `amount_charged` ni `monthly_price` el modal
               caería en `body_default`, cuyo copy lleva `/month` LITERAL fuera del
               placeholder y le diría «$8.99/mes» a quien acaba de pagar el ANUAL. Es la
               misma trampa que ya estaba documentada para Apple y se cierra igual:
               título, línea de referidos y botones. Cero copy nuevo, cero llaves. */
            window.history.replaceState({}, '', '/upgrade');
            await perfilFresco;
            /* Lo mismo que en H-3, y por lo mismo: ESTE CAMINO YA NO REDIRIGE. Mientras
               la vuelta de Stripe terminaba en `navigate('/dashboard')` daba igual que
               el memo se quedara viejo; ahora el usuario se queda en `/upgrade`, y sin
               volver a pedir se quedaría mirando la franja «Tienes 20% de descuento en
               tu próximo cobro» y el precio tachado DESPUÉS de haberlo consumido. */
            resetReferralState();
            fetchReferralState(userIdRef).then((s) => { setRefState(s); });
            setUpgradeSuccess({ plan: res.data.plan, sinCuerpo: true });
          }
        } catch {}
      }, 2000);
      return () => clearInterval(poll);
    }
    return undefined;
    // `t` is deliberately NOT a dependency: its identity changes on language
    // switch, which would tear down and restart an in-flight payment poll (and
    // re-show the overlay) just to relocalize a future toast. The poll keeps the
    // `t` captured at setup instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate, refreshUser, activa]);

  // Defensive cleanup: drop the overlay on unmount so it never sticks across navigations
  // (e.g., StrictMode double-effects, browser back/forward, or any edge case that bypasses
  // the polling effect's normal flow).
  useEffect(() => {
    return () => setAutoSplitOverlayVisible(false);
  }, []);

  /* ── AS-3 — LA COMPRA NATIVA (build de app) ────────────────────────────────
     Ni `/payments/checkout` ni `window.location.href`: la hoja de Apple, el JWS a
     /payments/apple/verify y el plan refrescado sin salir de la app. La cancelación del
     usuario (y una compra pendiente de aprobación) son SILENCIO absoluto. */
  const comprarConApple = async (targetPlan) => {
    const clave = billing === 'annual' ? 'pro_annual' : 'pro_monthly';
    setLoading(targetPlan);
    /* post-t7-2-dinero (B6, firma 3): justo antes de abrir la hoja de Apple se vuelve a preguntar
       el plan. Si ya es Pro (pago en la web hace un momento), no hay compra. */
    const fresco = await refreshUser();
    if (fresco && fresco.plan === 'pro') {
      toast.error(t('common:errors.pay_plan_already_active', { plan: 'Pro' }));
      setLoading(null);
      return;
    }
    logAnalyticsEvent('upgrade_checkout_started', 'completed',
      { plan: targetPlan, source: 'pricing_page', billing, provider: 'apple' });
    const res = await purchases.purchase(clave, { userId: user.id });
    if (res.status === 'ok') {
      await refreshUser();
      // AS-3.1 (R1, firmado) — el modal de éxito EXISTENTE, pero en app SÓLO con su
      // título y sus botones. El cuerpo no se pinta aquí y la razón es del texto, no del
      // dato: `body_default`/`body_prorated` llevan el signo `$` y el sufijo "/month"
      // LITERALES fuera del placeholder, así que con el importe de Apple mentirían en
      // dos frentes (una compra en pesos daría "$MX$169.00" y una anual, "/mes"). Cero
      // claves nuevas, cero copy nuevo. Sin `amount_charged` ni `monthly_price`: en app
      // no existe la mejora prorrateada de Stripe.
      setUpgradeSuccess({ plan: targetPlan });
    } else if (res.status === 'error') {
      toast.error(appleErrorCopy(res.error, t));
    }
    setLoading(null);
  };

  /* AS-3 — "Restaurar compras": la segunda red del cartero, a mano. */
  const restaurarCompras = async () => {
    setRestoring(true);
    const res = await purchases.restore();
    if (res.status === 'ok' && res.applied > 0) {
      await refreshUser();
      toast.success(t('pricing:iap.restore_success'));
    } else if (res.status === 'ok') {
      toast.info(t('pricing:iap.restore_empty'));
    } else {
      toast.error(appleErrorCopy(res.error, t));
    }
    setRestoring(false);
  };

  /* Stripe checkout or instant upgrade for existing subscribers */
  const handleUpgrade = async (targetPlan) => {
    if (!user || user.is_guest) { navigate('/signup'); return; }
    if (IS_APP_BUILD) { await comprarConApple(targetPlan); return; }
    setLoading(targetPlan);
    // Purchase instrumentation: fire-and-forget intent event BEFORE the checkout call so it
    // lands well ahead of the Stripe redirect (a full /payments/checkout round-trip separates them).
    logAnalyticsEvent('upgrade_checkout_started', 'completed', { plan: targetPlan, source: 'pricing_page', billing });
    try {
      // If user navigated to /upgrade from the AutoSplitModal "Hacer upgrade" CTA, propagate
      // the flag so the Stripe success_url includes &from_auto_split=true.
      let fromAutoSplit = false;
      try {
        const raw = sessionStorage.getItem('studybuddy_auto_split_intent');
        if (raw) {
          const intent = JSON.parse(raw);
          fromAutoSplit = intent?.intent_type === 'subscription_upgrade';
        }
      } catch { /* ignored */ }
      const body = { plan: targetPlan, origin_url: window.location.origin, billing };
      if (fromAutoSplit) body.from_auto_split = true;
      const res = await api.post('/payments/checkout', body);
      if (res.data.status === 'upgraded') {
        broadcastPlanChanged();  // nudge dashboard tabs to refetch (unarchive lags the webhook)
        await refreshUser();
        /* 28b-fix (H-3) — ESTE CAMINO NO REDIRIGE, ASÍ QUE EL MEMO SIGUE VIVO.
           La vuelta de Stripe es una carga de página completa y el memo nace
           limpio; la mejora instantánea, no. Sin esto la franja «Tienes 20% de
           descuento en tu próximo cobro» y el tachado seguían pintados DESPUÉS
           de que el descuento se consumió, y sólo un F5 lo arreglaba. */
        resetReferralState();
        fetchReferralState(userIdRef).then((s) => { setRefState(s); });
        setLoading(null);
        // Instant upgrade path (no Stripe redirect) — if from auto-split, hand off to /dashboard.
        if (fromAutoSplit) {
          window.location.href = '/dashboard?from_auto_split=true';
          return;
        }
        setUpgradeSuccess({
          plan: res.data.plan,
          amount_charged: res.data.amount_charged,
          monthly_price: res.data.monthly_price,
        });
      } else {
        window.location.href = res.data.url;
      }
    } catch (err) {
      // ── TANDA L / L4.2 (censo #20) — AQUÍ SE PINTABA EL `detail` CRUDO DEL SERVIDOR ──
      // El rechazo de pago de la mejora instantánea llega en un 402 con una frase LARGA y
      // en INGLÉS ("Your payment could not be processed. Your plan has not been changed…",
      // stripe_routes.py:179), y esta línea la mandaba al toast tal cual — también para un
      // usuario en español. Ese rechazo NO trae números (es una tarjeta declinada, no un
      // saldo de créditos), así que lo que se iguala con las otras tres superficies es el
      // idioma y el tono, no una cifra que no existe. `insufficientCreditsCopy` va delante
      // por si esta puerta llegara algún día a rechazar por créditos: entonces hablaría
      // como las demás, sin tocar esta línea otra vez.
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      /* post-t7-2-dinero (A6): con el pago fallido la compra se cierra hasta pagar esa factura;
         el aviso dice lo que pasa y Ajustes tiene el boton "Update card". */
      if (err.response?.data?.error_code === 'pay_update_card_first') {
        toast.error(t('common:errors.pay_update_card_first'));
        setLoading(null);
        navigate('/settings');
        return;
      }
      toast.error(
        insufficientCreditsCopy(detail, t)
        || (status === 402 ? t('modals:credit_pack.toasts.payment_declined') : null)
        || t('modals:credit_pack.toasts.checkout_failed')
      );
      setLoading(null);
    }
  };

  const openPacks = () => { if (isGuest) navigate('/signup'); else setCreditModal(true); };

  /* COS-I — EL SWIPE ENTRE PESTAÑAS. Ésta es la ÚLTIMA pestaña del orden: hacia
     la izquierda queda Analíticas y hacia la derecha no hay vecina (el hook no
     da la vuelta).

     EL CENSO DE CAPAS: la cortina de pago del auto-split —que existe justamente
     para que nadie navegue y cancele el sondeo—, el selector de paquetes y el
     modal de upgrade exitoso. Con cualquiera encima no nace ningún gesto.

     PAGER-2 — EL CENSO SE QUEDA AQUÍ, EL GESTO NO: lo monta UNA vez el carril
     (`components/PagerRaices.js`) y de aquí sólo sube este booleano. Sin carril
     —web, montaje suelto, arnés— `useReportarCapa` no hace absolutamente nada. */
  const capaEncima = autoSplitOverlayVisible || creditModal || !!upgradeSuccess;
  useReportarCapa(capaEncima);

  return (
    <div style={{ maxWidth: 940, margin: '0 auto', padding: '0 16px' }} data-testid="pricing-page">

      {/* ── Auto-split payment-processing blocking overlay ── */}
      {/* Renders only during /payments/status polling when sessionStorage intent_type is
          'subscription_upgrade'. Covers viewport including sidebar to prevent navigation
          that would cancel polling. Disappears automatically when polling resolves. */}
      {autoSplitOverlayVisible && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="auto-split-payment-overlay"
        >
          <div className="bg-[#141414] border border-[#262626] rounded-2xl p-8 max-w-md text-center">
            <Loader2 className="animate-spin text-[#FF6B00] mx-auto mb-4 w-12 h-12" data-testid="auto-split-payment-overlay-spinner" />
            <h3 className="text-white text-xl font-semibold mb-2" data-testid="auto-split-payment-overlay-title">
              {t('dashboard:auto_split.payment_processing_overlay_title', 'Procesando tu pago')}
            </h3>
            <p className="text-sm text-[#a0a0a0]" data-testid="auto-split-payment-overlay-body">
              {t('dashboard:auto_split.payment_processing_overlay_body', 'No salgas de esta página, te llevaremos al dashboard en un momento...')}
            </p>
          </div>
        </div>
      )}

      {/* ── Header (compacted so the packs section reaches the fold) ──
             28b-fix2 (B.2) — AQUÍ VIVÍA EL SUBTÍTULO, Y SE FUE.
             «Todos los planes incluyen la práctica adaptativa completa. Mejora
             para profundizar en lo que sabes.» prometía en prosa lo que la tabla
             de abajo ya dice mejor, con un renglón por capacidad y una columna
             por plan. La llave `headings.subtitle` salió de los dos locales.

             EL MARGEN DE ABAJO SUBE DE 6 A 16 PORQUE EL TÍTULO SE QUEDABA SOLO.
             Este margen colapsa con el del `h1` (2 px), que pasa a ser el último
             hijo, así que sin tocarlo el título quedaba a 6 px del interruptor.
             No se restituye el hueco entero de la frase porque ese hueco NO ES
             UN NÚMERO: medido en Chrome, la frase ocupaba 23.59 px a 1280 y
             39.19 a 375 —en móvil envolvía a dos renglones—, así que ningún
             margen fijo lo iguala en los dos anchos. 16 px es el aire mínimo
             pedido, y sale igual en los dos. ── */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <span
          style={{
            display: 'inline-block', fontSize: 12, fontWeight: 600,
            color: ORANGE_ACCENT, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 2,
          }}
          data-testid="upgrade-label"
        >
          {t('pricing:headings.eyebrow')}
        </span>
        <h1
          style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 2px', lineHeight: 1.2 }}
          data-testid="upgrade-headline"
        >
          <Trans i18nKey="pricing:headings.title">
            Study harder? No. Study <span style={{ color: '#ff8c32' }}>smarter.</span>
          </Trans>
        </h1>
      </div>

      {/* ── Billing toggle (monthly / annual) ── */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 2,
            background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 999, padding: 4,
          }}
          data-testid="billing-toggle"
        >
          <button
            type="button"
            aria-pressed={billing === 'monthly'}
            onClick={() => setBilling('monthly')}
            style={{
              padding: '8px 20px', borderRadius: 999, border: 'none', cursor: 'pointer',
              fontSize: 14, transition: 'background 0.15s, color 0.15s',
              fontWeight: billing === 'monthly' ? 600 : 500,
              background: billing === 'monthly' ? ORANGE : 'transparent',
              color: billing === 'monthly' ? '#fff' : '#999',
            }}
            data-testid="billing-monthly"
          >
            {t('pricing:billing_toggle.monthly')}
          </button>
          <button
            type="button"
            aria-pressed={billing === 'annual'}
            onClick={() => setBilling('annual')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 20px', borderRadius: 999, border: 'none', cursor: 'pointer',
              fontSize: 14, transition: 'background 0.15s, color 0.15s',
              fontWeight: billing === 'annual' ? 600 : 500,
              background: billing === 'annual' ? ORANGE : 'transparent',
              color: billing === 'annual' ? '#fff' : '#999',
            }}
            data-testid="billing-annual"
          >
            {t('pricing:billing_toggle.annual')}
            <span
              style={{
                fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                background: billing === 'annual' ? 'rgba(0,0,0,0.30)' : GREEN_BG,
                color: billing === 'annual' ? '#fff' : GREEN,
                transition: 'background 0.15s, color 0.15s',
              }}
              data-testid="billing-annual-badge"
            >
              {/* apple-sin-gratis (firma #781): en la app la pastilla no promete tiempo gratis (Apple 3.1.2(c):
                  la suscripcion no tiene prueba gratis); dice la etiqueta del paquete de 40. La web, igual. */}
              {IS_APP_BUILD ? t('pricing:packs.best_value') : t('pricing:billing_toggle.annual_badge')}
            </span>
          </button>
        </div>
      </div>

      {/* Discovery link (right under the toggle, visible without scrolling) — smooth-scrolls to packs */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <button
          onClick={() => document.getElementById('packs-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: 0, transition: 'color 0.15s' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = ORANGE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#999'; }}
          data-testid="packs-discover-link"
        >
          {t('pricing:packs.discover_link')}
        </button>
      </div>

      {/* ── AS-3 — el FALLO del catálogo de StoreKit (F-B). App-only: en web esta
             región no existe (`iapStatus` es null y la rama no se cumple).

             TANDA 16 (#199) — AQUÍ VIVÍA TAMBIÉN UN RENGLÓN DE «cargando precios»
             y se fue. Era un párrafo que aparecía y desaparecía por encima de las
             tarjetas: mientras StoreKit contestaba empujaba la página hacia abajo
             y al llegar la soltaba de golpe, o sea el precio que el usuario iba a
             leer se movía justo antes de que pudiera leerlo. Y no hacía falta
             decir nada: el hueco del precio YA reserva su lugar con su propio
             esqueleto (abajo, en cada tarjeta), que es la señal de carga honesta
             —está donde va a estar el dato y no mueve a nadie—. El aviso de
             ERROR se queda: ese sí tiene algo que decir y algo que ofrecer. ── */}
      {iapFailed && (
        <div style={{ textAlign: 'center', marginBottom: 12 }} data-testid="iap-error">
          <p style={{ fontSize: 13, color: '#999', margin: '0 0 8px' }}>{t('pricing:iap.load_failed')}</p>
          <button
            type="button"
            onClick={() => purchases.loadProducts()}
            style={{
              minHeight: 44, padding: '0 20px', borderRadius: 12, fontSize: 14, fontWeight: 600,
              border: `1px solid ${ORANGE}`, background: 'transparent', color: ORANGE, cursor: 'pointer',
            }}
            data-testid="iap-retry"
          >
            {t('pricing:iap.retry')}
          </button>
        </div>
      )}

      {/* ── TANDA 28b (firma #230, §G.1) — LA FRANJA DEL DESCUENTO ──────────────
          UNA franja para los dos lados del programa (el amigo que trae invitación
          y el que invita y ganó premio): los dos ven exactamente lo mismo, porque
          para lo que tienen que hacer a continuación —pagar— el hecho es el
          mismo. Dos textos distintos habrían obligado al frontend a adivinar cuál
          de los dos es el usuario, y `state` no lo dice.
          Mismas clases que la nota offline del bloque App Store
          (`AppStoreBloque.js:247`), que es la voz de aviso discreto de la casa.
          Sin descuento pendiente esta franja no existe y la página queda
          byte-idéntica a la de hoy. */}
      {descuentoPendiente && (
        <p
          /* 28b-fix (R-2b) — EL GRIS DE ESTA PÁGINA, NO EL DEL BLOQUE APP STORE.
             El texto discreto de `/upgrade` usa #999 en estilo en línea (la
             línea honesta de los packs, aquí abajo); esta franja estrenaba un
             tercer tono donde había dos. Mismo mecanismo y mismo valor. */
          className="text-[13px] leading-relaxed"
          /* 28b-fix2 (B.1) — EL AIRE VA AQUÍ, Y SÓLO POR ABAJO.
             El medallón «RECOMENDADO» no vive dentro de la tarjeta Pro: la
             desborda por arriba con `top: -12`, así que el techo visible de esa
             zona está 12 px más arriba del borde contra el que se mide este
             margen. Con los 14 de antes quedaban 2 px de aire nominal (3.00
             medidos en Chrome a 1280 y a 375) y la franja tocaba el medallón.
             32 − 12 = 20, que es el mínimo pedido.
             Por arriba se queda en cero a propósito: subirlo movería la
             distancia con la línea de paquetes de aquí encima (17 px medidos),
             que esta tanda no toca. Y como la franja sólo existe con descuento
             pendiente, quien no lo tiene no ve moverse absolutamente nada. */
          style={{ maxWidth: 720, margin: '0 auto 32px', textAlign: 'center', color: '#999' }}
          data-testid="referral-discount-banner">
          {t('pricing:referral.banner_discount')}
        </p>
      )}

      {/* ── Plan Cards (2-up) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 20,
          maxWidth: 720,
          margin: '0 auto',
        }}
        className="pricing-grid"
        data-testid="plan-cards-grid"
      >
        {PLANS.map((plan) => {
          const isPro = plan.key === 'pro';
          /* 28d (§D.1) — el plan de mentira es SÓLO de la tarjeta Pro. La de Free
             sigue leyendo el real, así que su botón no cambia en modo vista. */
          const planDeLaTarjeta = isPro ? planParaPintar : currentPlan;
          const isCurrent = planDeLaTarjeta === plan.key;
          const isUpgrade = PLAN_ORDER[plan.key] > PLAN_ORDER[planDeLaTarjeta];
          // WEB: la constante de siempre. APP: el precio de Apple, `priceString` tal cual.
          //
          // APPLE 3.1.2(c) (#100a) — EL NUMERO GRANDE DEL ANUAL ES EL TOTAL FACTURADO.
          // Antes esta tarjeta pintaba aqui el equivalente POR MES del anual ($9.17) y
          // dejaba el facturado ($109.99) en la linea chica de abajo; Apple rechazo el
          // build 83 por eso. Ahora sube el facturado y el por-mes baja a la secundaria.
          // El mensual no se toca: ahi `priceString` YA es el monto facturado.
          const productoDelCiclo = billing === 'annual' ? proAnual : proMensual;
          const anualPro = isPro && billing === 'annual';
          let price = isPro
            ? (anualPro ? WEB_ANNUAL_TOTAL : PRO_PRICING[billing].display)
            : '$0';
          if (IS_APP_BUILD && isPro) {
            price = (iapReady && productoDelCiclo)
              ? productoDelCiclo.priceString
              : null;      // sin cifra: el hueco lo cubre el esqueleto
          }
          const moneda = (IS_APP_BUILD && iapReady && productoDelCiclo)
            ? productoDelCiclo.currencyCode : 'USD';
          const showAnnualDetail = isPro && billing === 'annual';
          // F-H (crossgrade) y F-C (Pro por Stripe): ver la cabecera del componente.
          const crossgrade = appleProvider && isPro && isCurrent
            && !!idVigente && idVigente !== idSeleccionado;
          const mostrarActual = isCurrent && !crossgrade;
          /* TANDA 28b-fix3 (firma #263) — LA MISMA CONDICIÓN QUE YA DECIDÍA EL TACHADO,
             CON NOMBRE. Es literalmente la de más abajo (`tacharPrecio && isPro &&
             !mostrarActual`); se nombra una vez para que las cuatro piezas que #263
             mueve —el par de precios, el sufijo, el bloque anual y la nota— no puedan
             desalinearse nunca: o las cuatro están en modo descuento, o ninguna. Fuera
             de ella la tarjeta es byte-idéntica a la de hoy. */
          const conDescuentoEnLaTarjeta = tacharPrecio && isPro && !mostrarActual;
          const mostrarCompra = isUpgrade || crossgrade;
          /* 28d (§D.2) — EL CANDADO DE SEGURIDAD. En modo vista el botón se pinta con
             su texto normal —es lo que se está mirando— pero no compra: la vista es un
             molde para ver la cara, no un carril de cobro, y un clic ahí abriría un
             checkout de verdad contra la cuenta real de Carlos, que es Pro y no debería
             estar comprando nada. El `onClick` además se retira, no sólo se deshabilita
             el botón: dos cierres para una puerta que da a un cobro. */
          const compraBloqueada = (IS_APP_BUILD && !iapReady) || enVistaRef;

          return (
            <div
              key={plan.key}
              className={`plan-card ${isPro ? 'plan-card--pro' : ''}`}
              style={{
                position: 'relative',
                background: plan.featured ? '#161311' : '#111',
                border: `${plan.featured ? '1.5px' : '1px'} solid ${plan.featured ? ORANGE : '#262626'}`,
                borderRadius: 18,
                padding: '22px 22px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                boxShadow: plan.featured ? `0 0 40px -12px rgba(255,107,0,0.22)` : 'none',
              }}
              data-testid={`plan-card-${plan.key}`}
            >
              {/* RECOMMENDED badge */}
              {plan.featured && (
                <span
                  style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: ORANGE, color: '#fff', fontSize: 12, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5, padding: '5px 18px', borderRadius: 999,
                    whiteSpace: 'nowrap', zIndex: 2,
                  }}
                  data-testid="recommended-badge"
                >
                  {t('pricing:badge.recommended')}
                </span>
              )}

              {/* Plan name */}
              <div
                style={{
                  fontSize: 13, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase',
                  color: isPro ? ORANGE_ACCENT : '#999',
                  marginBottom: 10,
                }}
              >
                {t(`pricing:plans.${plan.key}.name`)}
              </div>

              {/* Price (dominant) */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                {price === null ? (
                  /* AS-3 (F-B): cargando/error → esqueleto en el hueco del precio, SIN cifra.
                     Jamás el número de la web como respaldo. */
                  <span
                    style={{
                      display: 'inline-block', width: 110, height: 40, borderRadius: 10,
                      background: '#1c1c1c',
                    }}
                    aria-label={t('pricing:iap.loading')}
                    data-testid={`price-skeleton-${plan.key}`}
                  />
                ) : conDescuentoEnLaTarjeta ? (
                  /* 28b-fix (#262) — UN PRO NO VE EL TACHADO.
                     `isPro` es la TARJETA, no el usuario, así que un suscriptor
                     Pro con descuento pendiente veía «$8.99 tachado → $7.19»
                     encima de un botón que dice «Plan actual»: una oferta de
                     compra que no puede tomar. Su descuento es real —se aplica
                     en la renovación— y por eso la FRANJA se le sigue pintando;
                     lo que se va es el precio de venta tachado.
                     `mostrarActual` es EXACTAMENTE el valor con el que esta
                     misma tarjeta decide decir «Plan actual» (se lee más
                     abajo): así las dos cosas no pueden desalinearse nunca.

                     TANDA 28b (§G.1) — EL TACHADO, Y EL LITERAL NO SE EDITA.
                     El número de arriba sigue siendo EXACTAMENTE el mismo string
                     de `PRO_PRICING` / `WEB_ANNUAL_TOTAL`; sólo se envuelve en
                     `line-through` y se pinta al lado el resultado de
                     `0.8 × literal`. Los once precios en duro del frontend son
                     zona intocable por diseño, y el día que Pro cambie de precio
                     el descuento cuadra solo porque se CALCULA de él.
                     Mismo tipo de letra para los dos, y 8 px de separación: el
                     precio nuevo tiene que leerse como el que se va a cobrar.

                     TANDA 28b-fix3 (firma #263) — EL VIEJO SE ENCOGE, Y NO ES ESTÉTICA.
                     Con los dos a 44 px la fila NO CABE, medido en las ocho
                     combinaciones: a 1280 el carril del grid es fijo (`minmax(0,1fr)`
                     sobre 720 px → 350 px) y el contenido se RECORTA —en el anual en
                     inglés el `USD` desaparecía del recorte, +21 px de desborde—; a 375
                     la media query de abajo usa `1fr` pelado, que es `minmax(auto,1fr)`
                     y por tanto tiene min-width automático, así que el carril crece
                     hasta el `min-content` de esta fila y EMPUJA EL `<main>` a scroll
                     horizontal (+42 ES / +46 EN). El sufijo, además, se partía en dos
                     renglones (21 → 42 px de alto): ése es el «se sale el texto» que se
                     ve a simple vista.
                     20 px y `#777` dejan al viejo como lo que es —la referencia
                     tachada— y al nuevo como el protagonista. La línea base la comparten
                     porque el contenedor ya es `align-items: baseline`. */
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    {/* #263 — EL NUEVO VA PRIMERO. Hasta la 28b el tachado abría la fila
                        y el precio real venía detrás; con el viejo ya encogido, dejarlo
                        delante pondría de protagonista al número que NO se va a cobrar.
                        El orden del documento es el orden de lectura. */}
                    <span
                      style={{ fontSize: 44, fontWeight: 700, color: '#fff', lineHeight: 1 }}
                      data-testid={`plan-price-${plan.key}-new`}
                    >
                      {conDescuento(price)}
                    </span>
                    <span
                      className="line-through"
                      style={{ fontSize: 20, fontWeight: 600, color: '#777', lineHeight: 1 }}
                      data-testid={`plan-price-${plan.key}-old`}
                    >
                      {price}
                    </span>
                  </span>
                ) : (
                  <span
                    style={{ fontSize: 44, fontWeight: 700, color: '#fff', lineHeight: 1 }}
                    data-testid={`plan-price-${plan.key}`}
                  >
                    {price}
                  </span>
                )}
                {/* #263 — CON DESCUENTO EL SUFIJO NO SE PINTA. Es la pieza que más
                    ancho devuelve (24-42 px) y la única que se partía en dos renglones,
                    y con dos precios delante ya no sabe a cuál de los dos se refiere.
                    Lo que decía —mensual o anual, y cuánto se cobra después— lo dice
                    entera la nota de abajo. */}
                {!conDescuentoEnLaTarjeta && (
                  <span style={{ fontSize: 14, color: '#888' }} data-testid={`plan-period-${plan.key}`}>
                    {t(`pricing:period.${isPro ? (anualPro ? 'year' : 'month') : 'forever'}`)}
                  </span>
                )}
                {/* La moneda SÍ se queda con descuento: es la que evita leer «$71.92»
                    como pesos. Estrena `data-testid` para poder afirmar en la medición
                    que sigue DENTRO de la tarjeta (a 1280 se salía del recorte). */}
                <span style={{ fontSize: 12, color: '#666' }} data-testid={`plan-currency-${plan.key}`}>{moneda}</span>
              </div>

              {/* #263 — LA NOTA. Una sola línea que sustituye al sufijo y al bloque
                  `annual.*`, y que además dice lo que ninguno de los dos decía: que el
                  20% es del PRIMER cobro y que después se vuelve al precio de lista.
                  CERO cifras a mano: `{{new}}` es el mismo `conDescuento(price)` de
                  arriba y `{{old}}` el mismo literal (`PRO_PRICING` / `WEB_ANNUAL_TOTAL`)
                  que ya está tachado al lado, así que el día que Pro cambie de precio la
                  nota cuadra sola. */}
              {conDescuentoEnLaTarjeta && (
                <div
                  style={{ fontSize: 13, color: '#999', marginTop: 8 }}
                  data-testid="referral-price-note"
                >
                  {t(`pricing:referral.price_note_${anualPro ? 'annual' : 'monthly'}`,
                    { new: conDescuento(price), old: price })}
                </div>
              )}

              {/* Annual detail (Pro only). Both modes are a single line now, so the reserved height
                  is small and the toggle never jumps the card.
                  #263 — CON DESCUENTO NO SE PINTA NINGUNA DE LAS DOS RAMAS. `monthly_hint`
                  («o $89.90/año…») y `monthly_equivalent` («Equivale a $7.49/mes») son
                  precios de LISTA: puestos debajo de un $7.19, el usuario acaba leyendo
                  tres cifras distintas para lo mismo. La nota de arriba ocupa su sitio y
                  dice la única que importa mientras haya cupón. La píldora del ahorro
                  anual se va con ellas, por el mismo motivo. */}
              {isPro && !conDescuentoEnLaTarjeta && (
                <div style={{ marginTop: 8, minHeight: 24 }} data-testid="annual-detail">
                  {totalAnual === null ? null : showAnnualDetail ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {/* #100a: la linea subordinada. Antes decia "Facturado X al ano"
                          porque el facturado no estaba arriba; ahora SI esta, y lo que
                          falta abajo es el equivalente por mes. */}
                      <span style={{ fontSize: 13, color: '#999' }}>{t('pricing:annual.monthly_equivalent', { price: porMesAnual })}</span>
                      <span
                        style={{
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                          background: GREEN_BG, color: GREEN,
                        }}
                        data-testid="annual-save-chip"
                      >
                        {t('pricing:annual.save_chip', { amount: ahorroAnual })}
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#999' }} data-testid="annual-monthly-hint">{IS_APP_BUILD ? t('pricing:annual.monthly_hint_app', { price: totalAnual }) : t('pricing:annual.monthly_hint', { price: totalAnual })}</div>
                  )}
                </div>
              )}

              {/* TANDA 28d (§F) — AQUÍ VIVÍA EL PÁRRAFO DE DESCRIPCIÓN DEL PLAN.
                  Se eliminó de las dos tarjetas y de las tres superficies donde se
                  pintaba (`/upgrade` web, `/upgrade` en build de app y `/pricing`
                  público), junto con sus llaves `pricing:plans.*.description`.
                  EL HUECO, MEDIDO: el párrafo separaba 14 px del bloque de precio y
                  12 px de la raya. Sin él la raya quedaría PEGADA (0 px) al bloque de
                  arriba, así que se queda con los 14 px que había entre el precio y
                  lo que venía después. La tarjeta encoge 52 px, que es justo el sitio
                  que ocupaba el texto. */}
              {/* Divider */}
              <div style={{ height: 1, background: '#1f1f1f', marginTop: 14, marginBottom: 8 }} />

              {/* Feature list */}
              <div style={{ flex: 1, marginBottom: 14 }}>
                {FEATURES.map((feat) => {
                  const val = feat[plan.key];
                  const included = val === true || typeof val === 'string';
                  return (
                    <div
                      key={feat.key}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13 }}
                    >
                      {included ? <CheckIcon /> : <XIcon />}
                      <span style={{ color: included ? '#ddd' : '#3a3a3a' }}>
                        {typeof val === 'string' ? (
                          <>
                            <span style={{ color: '#fff', fontWeight: 700 }}>{val}</span>
                            <span> {t(`pricing:features.${feat.key}`)}</span>
                          </>
                        ) : feat.porPlan ? (
                          conSabiEnNegritas(t(`pricing:features.${feat.key}_${plan.key}`))
                        ) : (
                          t(`pricing:features.${feat.key}`)
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* CTA Button — anchored to bottom */}
              <div style={{ marginTop: 'auto' }}>
                {mostrarActual ? (
                  <button
                    disabled
                    style={{
                      width: '100%', padding: '11px 0', borderRadius: 12,
                      background: '#1a1a1a', color: '#555', fontSize: 14, fontWeight: 500,
                      border: '1px solid #252525', cursor: 'default',
                    }}
                    data-testid={`current-plan-btn-${plan.key}`}
                  >
                    {t('pricing:cta.current_plan')}
                  </button>
                ) : (mostrarCompra && isPro && stripeManaged) ? (
                  /* F-C — Pro cobrado por Stripe dentro de la app: aquí no se vende la
                     suscripción otra vez. Los PAQUETES no se tocan (FIRMA #26/F-5). */
                  <p style={{ fontSize: 12, color: '#999', lineHeight: 1.5, margin: 0 }}
                     data-testid="iap-stripe-managed">
                    {t('pricing:iap.stripe_managed')}
                  </p>
                ) : mostrarCompra ? (
                  <>
                  <button
                    onClick={enVistaRef ? undefined : () => handleUpgrade(plan.key)}
                    disabled={!!loading || compraBloqueada}
                    style={{
                      width: '100%', padding: '11px 0', borderRadius: 12, fontSize: 14, fontWeight: 600,
                      border: isPro ? 'none' : `1px solid ${ORANGE}`,
                      background: isPro ? ORANGE : 'transparent',
                      color: isPro ? '#fff' : ORANGE,
                      cursor: loading ? 'wait' : 'pointer',
                      transition: 'background 0.2s, transform 0.15s, color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (isPro) { e.currentTarget.style.background = '#ff8636'; }
                      else { e.currentTarget.style.background = 'rgba(255,107,0,0.08)'; }
                    }}
                    onMouseLeave={(e) => {
                      if (isPro) { e.currentTarget.style.background = ORANGE; }
                      else { e.currentTarget.style.background = 'transparent'; }
                    }}
                    data-testid={`upgrade-btn-${plan.key}`}
                  >
                    {loading === plan.key ? (
                      <Loader2 style={{ width: 16, height: 16, margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                    ) : (
                      t('pricing:cta.upgrade_to', { plan: t(`pricing:plans.${plan.key}.name`) })
                    )}
                  </button>
                  <LegalAcceptance variant="purchase" className="mt-2" />
                  </>
                ) : !isGuest ? (
                  /* Downgrade: plan is lower than current — hidden for guests */
                  <button
                    onClick={() => navigate('/settings')}
                    style={{
                      width: '100%', padding: '11px 0', borderRadius: 12, fontSize: 14, fontWeight: 500,
                      background: 'transparent', color: '#555', border: '1px solid #252525', cursor: 'pointer',
                    }}
                    data-testid={`downgrade-btn-${plan.key}`}
                  >
                    {t('pricing:cta.downgrade')}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Credit packs section ── */}
      <div id="packs-section" style={{ maxWidth: 720, margin: '24px auto 0', textAlign: 'center', scrollMarginTop: 16 }} data-testid="packs-section">
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 14 }}>
          {t('pricing:packs.section_title')}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }} className="packs-grid">
          {PACKS_DISPLAY.map((pack) => {
          const packProducto = producto(pack.key);
          const packPrecio = IS_APP_BUILD
            ? ((iapReady && packProducto) ? packProducto.priceString : null)
            : `$${pack.price}`;
          const packMoneda = (IS_APP_BUILD && iapReady && packProducto)
            ? packProducto.currencyCode : 'USD';
          return (
            <button
              key={pack.credits}
              onClick={openPacks}
              className="pack-card"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
                background: '#111', border: '1px solid #262626', borderRadius: 14, padding: 16,
                cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = ORANGE; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#262626'; }}
              data-testid={`pack-card-${pack.credits}`}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{pack.credits}</span>
                <span style={{ fontSize: 13, color: '#999' }}>{t('pricing:packs.credits_word')}</span>
              </div>
              {packPrecio === null ? (
                <span
                  style={{ display: 'inline-block', width: 72, height: 18, borderRadius: 6, background: '#1c1c1c' }}
                  aria-label={t('pricing:iap.loading')}
                  data-testid={`pack-price-skeleton-${pack.credits}`}
                />
              ) : (
                <span style={{ fontSize: 14, color: '#ccc' }}>{packPrecio} <span style={{ fontSize: 12, color: '#666' }}>{packMoneda}</span></span>
              )}
              {pack.best && (
                <span
                  style={{
                    marginTop: 4, fontSize: 12, fontWeight: 600, padding: '3px 10px',
                    borderRadius: 999, background: GREEN_BG, color: GREEN,
                  }}
                  data-testid="pack-best-value"
                >
                  {t('pricing:packs.best_value')}
                </span>
              )}
            </button>
          );
        })}
        </div>
        <p style={{ fontSize: 13, color: '#999', marginTop: 14, lineHeight: 1.5 }}>
          {t('pricing:packs.honest_line')}
        </p>

        {/* LAPIDA post-t7-2c (E1): aqui vivia la linea discreta "Invita a un
            amigo" (diseño §7.2 de la 28b); el programa ya no admite a nadie nuevo. */}
      </div>

      {/* ── Bottom Section — different for guests vs logged-in ── */}
      {isGuest ? (
        <p style={{ textAlign: 'center', fontSize: 12, color: '#555', marginTop: 18 }} data-testid="guest-cta">
          <Trans i18nKey="pricing:bottom.guest_cta">
            <button
              onClick={() => navigate('/signup')}
              style={{
                background: 'none', border: 'none', color: '#ff8c32',
                cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0,
              }}
              data-testid="guest-signup-link"
            >
              Create an account
            </button>
            {' '}to unlock credit packs and manage your subscription
          </Trans>
        </p>
      ) : (
        <p style={{ textAlign: 'center', fontSize: 12, color: '#555', marginTop: 18 }}>
          <Trans i18nKey="pricing:bottom.downgrade_cta">
            <button
              onClick={() => navigate('/settings')}
              style={{
                background: 'none', border: 'none', color: '#ff8c32',
                cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0,
              }}
              data-testid="settings-link"
            >
              Settings
            </button>
          </Trans>
        </p>
      )}

      {/* ── AS-3 — Restaurar compras (app-only) ──
          Mismo tratamiento visual que el link de descubrimiento de paquetes de esta misma
          página (el botón `packs-discover-link` de arriba: sin fondo, sin borde, gris que
          se enciende en naranja), más `minHeight: 44` para que la zona táctil cumpla los
          44pt. No necesita safe-area propia: el colchón del chasis
          (CONTENT_CLEARANCE_CLASS en App.js) ya deja libre la huella de la píldora. */}
      {IS_APP_BUILD && !isGuest && (
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button
            type="button"
            onClick={restaurarCompras}
            disabled={restoring}
            style={{
              background: 'none', border: 'none', cursor: restoring ? 'wait' : 'pointer',
              fontSize: 14, color: '#999', padding: '0 12px', minHeight: 44,
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = ORANGE; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#999'; }}
            data-testid="iap-restore-btn"
          >
            {t('pricing:iap.restore_btn')}
          </button>
        </div>
      )}

      {/* Credit Pack Modal */}
      <CreditPackModal open={creditModal} onClose={() => setCreditModal(false)} />

      {/* Instant Upgrade Success Modal */}
      {upgradeSuccess && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="upgrade-success-modal">
          <div className="bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl text-center">
            <CircleCheckBig className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-3" data-testid="upgrade-success-title">
              {t('pricing:modal.upgrade_success.title', { plan: t(`pricing:plans.${upgradeSuccess.plan}.name`) })}
            </h3>
            {/* AS-3.1 (R1) — EL CUERPO ES DE LA WEB. Sus dos claves llevan el `$` y el
                "/month" LITERALES fuera del placeholder (`<1>${{price}}/month</1>`), así
                que con el importe localizado de Apple mentirían: "$MX$169.00" en un
                storefront de pesos, y "/mes" en una compra ANUAL. En app el modal se
                queda con su título y sus botones —cero copy nuevo—, y la confirmación
                del importe la da la propia hoja de Apple.

                TANDA 28b-fix3 (bloque B) — `sinCuerpo` ES EL MISMO TRATO, POR LA MISMA
                RAZÓN. La vuelta de Stripe abre esta ventana con lo único que
                `/payments/status` devuelve: `{status, payment_status, plan}`. Sin
                `amount_charged` ni `monthly_price` caería en `body_default`, que lleva
                «/month» LITERAL fuera del placeholder — o sea que a quien acaba de pagar
                el ANUAL le diría «se te cobrará $8.99/mes». Antes que un cuerpo que
                miente, ninguno: el título, la línea de invitar y los botones dicen todo
                lo que hay que decir. */}
            {!IS_APP_BUILD && !upgradeSuccess.sinCuerpo && (
            <p className="text-sm text-[#a0a0a0] leading-relaxed mb-6" data-testid="upgrade-success-body">
              {upgradeSuccess.amount_charged != null ? (
                <Trans
                  i18nKey="pricing:modal.upgrade_success.body_prorated"
                  values={{ amount: upgradeSuccess.amount_charged.toFixed(2), price: upgradeSuccess.monthly_price ?? '8.99' }}
                >
                  Your upgrade is complete. Today you were charged <span className="text-white font-medium">${'{{amount}}'}</span>, we discounted the time you already paid on your previous plan. From next month, you'll be charged <span className="text-white font-medium">${'{{price}}'}/month</span>.
                </Trans>
              ) : (
                <Trans
                  i18nKey="pricing:modal.upgrade_success.body_default"
                  values={{ price: upgradeSuccess.monthly_price ?? '8.99' }}
                >
                  Your upgrade is complete. You were only charged for the remaining time in your current billing period. From next month, you'll be charged <span className="text-white font-medium">${'{{price}}'}/month</span>.
                </Trans>
              )}
            </p>
            )}
            {/* LAPIDA post-t7-2c (E1): aqui vivia la linea de invitar de la ventana
                de exito (diseño §7.6 de la 28b), que abria "Invita a un amigo". */}
            <div className="flex gap-3">
              <button onClick={() => { setUpgradeSuccess(null); navigate('/dashboard'); }}
                className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#e55f00] text-white font-medium rounded-xl h-10 text-sm transition-colors"
                data-testid="upgrade-success-dashboard">
                {t('pricing:cta.go_to_dashboard')} <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => setUpgradeSuccess(null)}
                className="flex-1 border border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl h-10 text-sm transition-colors"
                data-testid="upgrade-success-dismiss">
                {t('pricing:cta.got_it')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive + motion */}
      <style>{`
        .plan-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        /* #149c (2026-08-25) — SÓLO SE ENVUELVE; LOS 2 px SE QUEDAN.
           Esta hoja se inyecta desde el JSX, así que el flag
           future.hoverOnlyWhenSupported de tailwind.config.js no la ve: la compuerta
           va a mano. Y hacía falta: /upgrade es una pantalla AUTENTICADA que en el
           build de app sirve PagerRaices, o sea que estos 2 px llegan al iPhone —
           tocabas una tarjeta de plan, subía, y se quedaba arriba.
           El translateY es decisión de diseño APLAZADA: en escritorio se comporta
           exactamente como hoy. No lo quites aquí sin firma. */
        @media (hover: hover) {
          .plan-card:hover { transform: translateY(-2px); }
        }
        @media (max-width: 768px) {
          .pricing-grid { grid-template-columns: 1fr !important; }
          .plan-card--pro { order: -1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .plan-card { transition: none; }
          .plan-card:hover { transform: none; }
        }
      `}</style>
    </div>
  );
}
